/**
 * Standard event names used across the application
 * Use these constants to avoid typos and enable IDE autocomplete
 */
export const EVENTS = {
  // Editor events
  EDITOR_STATE_CHANGED: 'editor:stateChanged',
  EDITOR_ENTITY_PLACED: 'editor:entityPlaced',

  // Camera events
  CAMERA_SETTINGS_CHANGED: 'camera:settingsChanged',

  // Game/Network Intent Events
  PLAYER_ACTION_INTENT: 'player:actionIntent',
  PLAYER_CHAT_INTENT: 'player:chatIntent',
  SERVER_STATE_UPDATE: 'server:stateUpdate',

  // Game events
  PLAYER_DAMAGED: 'player:damaged',
  PLAYER_HEALED: 'player:healed',
  PLAYER_LEVEL_UP: 'player:levelUp',
  PLAYER_GAIN_XP: 'player:gainXP',
  PLAYER_DIED: 'player:died',
  PLAYER_RESPAWNED: 'player:respawned',
  VICTORY_ACHIEVED: 'game:victoryAchieved',
  PLAYER_DEATH_HANDLED: 'game:playerDeathHandled',
  VICTORY_HANDLED: 'game:victoryHandled',
  GAME_SCENE_READY: 'game:sceneReady',
  /** Rising-edge activity from a standard-mapped browser gamepad. Consumers
   *  may react to the activity without owning or swallowing gameplay input. */
  GAMEPAD_ACTIVITY: 'input:gamepadActivity',

  // Map/Level events
  LEVEL_LOADED: 'level:loaded',
  LEVEL_HOT_RELOADED: 'level:hotReloaded',
  ITEM_PICKED_UP: 'level:itemPickedUp',
  AMBIENT_SOUND_CHANGED: 'level:ambientSoundChanged',
  LEVEL_UNLOADED: 'level:unloaded',
  /** Screensaver biome-name caption — emitted sticky by ScreensaverSystem so the
   *  React ScreensaverOverlay (which mounts AFTER kernel boot) receives the
   *  first biome's caption via replay-on-subscribe. Replaces the stale
   *  globalThis.__screensaverOverlay handle for the caption path. */
  SCREENSAVER_CAPTION: 'screensaver:caption',
  /** Scene-mood dial — game code sets ambient intensity (day/night, combat,
   *  event). AtmosphereLayer multiplies it into every layer's modulators. */
  SCENE_MOOD: 'level:sceneMood',
  /** Combat-mood pulse — CombatMoodDriver raises this on cast heat (a
   *  presentation signal, not netcode); AtmosphereLayer MULTIPLIES it onto the
   *  per-level scene mood so the ambiance intensifies during fights and eases
   *  back in a lull. Orthogonal to (does not overwrite) the base SCENE_MOOD. */
  COMBAT_MOOD: 'level:combatMood',
  /** Boss-mood swell — BossMoodDriver raises this on boss phase transitions
   *  (from the server `bossPhaseChange` broadcast). AtmosphereLayer MULTIPLIES
   *  it onto the per-level mood alongside sceneMood × combatMood so the
   *  ambiance swells as a boss fight escalates and settles on defeat.
   *  Orthogonal to (does not overwrite) SCENE_MOOD or COMBAT_MOOD. */
  BOSS_MOOD: 'level:bossMood',
  /** World-combat reactivity pulse — a combat beat (big cast / heavy impact /
   *  kill / reaction) the atmosphere may answer with transient, decay-to-1
   *  envelopes + positional handle kicks (AtmosphereReactivityDriver). Emitted
   *  through the bus (reactivityPulse helper) so combat code never imports the
   *  atmosphere. See docs/plans/fx-grammar-specs/world-combat-reactivity.md. */
  ATMOSPHERE_PULSE: 'level:atmospherePulse',
  /** Time-of-day phase 2 — TimeOfDayDriver raises this each tick with the eased
   *  day-phase + time-keyed colorGrade palette so AtmosphereLayer can (a) blend
   *  the authored biome colorGrade toward the time grade (night = cool-dark) and
   *  (b) apply per-layer day-response multipliers (godRays dim at night, fog
   *  thickens). Interiors + always-night maps + reduce-motion opt out (the driver
   *  does not emit) so exempt scenes keep their authored eternal light. */
  TIME_OF_DAY: 'level:timeOfDay',

  // Quest events
  QUEST_COMPLETED: 'quest:completed',
  QUEST_DISCOVERED_CHANGED: 'quests:discovered_changed',
  BOSS_PHASE_CHANGED: 'boss:phaseChanged',
  /** A phased boss leashed home and is back to its base phase (plan 07). */
  BOSS_PHASE_RESET: 'boss:phaseReset',
  EXPERIENCE_GAINED: 'player:experienceGained',
  XP_SHRINE_ACTIVATED: 'player:xpShrineActivated',
  // Emitted when a level-gated portal blocks an under-level player.

  // Player interaction events

  // Mob events
  MOB_DIED: 'mob:died',

  // NPC events

  // Combat events
  COMBAT_NPC_DAMAGED: 'combat:npcDamaged',
  /**
   * Unified damage event emitted by DamageSyncSystem (Phase C.1,
   * 2026-05-12). Listeners should prefer this over the raw
   * COMBAT_NPC_DAMAGED + PLAYER_DAMAGED pair: the shape is the same
   * `DamageEvent` (src/shared/network/DamageEvent.ts) regardless of
   * target kind, and a single subscription replaces two.
   */
  DAMAGE_APPLIED: 'combat:damageApplied',
  TARGET_CHANGED: 'combat:targetChanged',
  /** Display values changed for the held target; does not reset per-target combat state. */
  TARGET_INFO_UPDATED: 'combat:targetInfoUpdated',
  NPC_DEFEATED: 'combat:npcDefeated',
  ABILITY_CASTED: 'combat:abilityCasted',
  /** A locally predicted ability passed every client gate and executed.
   *  Presentation-only: GameAudioSystem consumes this for the casting
   *  player's immediate cast cue. Remote casts use ABILITY_CASTED, whose
   *  server broadcast deliberately excludes the originating client. */
  AUDIO_ABILITY_CAST: 'audio:abilityCast',
  /** A REMOTE player's travel-mode dash (Charge/Heroic Leap) was confirmed by
   *  the server (`dashed` broadcast, travelMs > 0, non-local session).
   *  Payload {sessionId, x, y, abilityId, travelMs} — x/y is the (possibly
   *  wall-clamped) destination. VFXSystem renders the trail + landing beat so
   *  other clients see the dash, not a silent position glide. */
  REMOTE_DASHED: 'combat:remoteDashed',
  /** Local combo-point (assassin star) pool changed — payload {points, max}.
   *  Emitted by ComboPointSystem; consumed by the unit-frame star counter. */
  COMBO_POINTS_CHANGED: 'combat:comboPointsChanged',
  /** Local throwing-star rack state changed — payload {playerId, used, remaining, max, reloading, readyAt, reloadRemainingMs}.
   *  Emitted by ThrowingStarRack; consumed by the HUD star counter and action bar. */
  THROWING_STARS_CHANGED: 'combat:throwingStarsChanged',
  // Wire-relay variants — fire ONCE per server broadcast (every player's
  // event arrives here). Distinct from the local-lifecycle PLAYER_DIED /
  // PLAYER_RESPAWNED above which fire only for the LOCAL player's
  // own death/respawn cycle. CombatLogService consumes the wire-relay
  // variants so it sees every player's events; GameStateManager + UI
  // overlays stay subscribed to the local-lifecycle variants.
  WIRE_PLAYER_DIED: 'wire:playerDied',
  WIRE_PLAYER_RESPAWNED: 'wire:playerRespawned',
  /** Server StatusEffectService broadcasts — fire ONCE per status apply/
   *  remove on ANY target. Until 2026-06-12 these wire messages had NO client
   *  consumer: the caster's own client drew the target's debuff aura via the
   *  local StatusEffectManager prediction, but the VICTIM and every OBSERVER
   *  rendered nothing. VFXSystem consumes these for the non-caster clients
   *  (parity rule: remote VFX driven by events + data, never damage). */
  WIRE_STATUS_APPLIED: 'wire:statusApplied',
  WIRE_STATUS_REMOVED: 'wire:statusRemoved',
  /** Server StatusEffectService per-tick broadcast. Carries `damage` for
   *  DoT ticks or `heal` for HoT ticks. Consumed by DamagePresentationService
   *  to render HoT heal FCT (DoT FCT already comes via playerDamaged from
   *  CombatPipeline). */
  WIRE_STATUS_TICK: 'wire:statusTick',
  /** Authoritative successful consumable activation, broadcast to observers
   *  so potion/use bursts are world-visible rather than local-only UI state. */
  WIRE_CONSUMABLE_USED: 'wire:consumableUsed',

  // Inventory events
  INVENTORY_ITEM_EQUIPPED: 'inventory:itemEquipped',
  INVENTORY_ITEM_UNEQUIPPED: 'inventory:itemUnequipped',
  INVENTORY_ITEM_CONSUMED: 'inventory:itemConsumed',
  INVENTORY_GOLD_CHANGED: 'inventory:goldChanged',

  // NPC events

  // Input events

  // UI events
  UI_ACTION: 'ui:action',
  COMBAT_MELEE_HIT: 'combat:melee_hit',
  /** Plan 29 F4: the server confirmed the local player's melee hit (impact + hit sound). */
  COMBAT_MELEE_HIT_CONFIRMED: 'combat:melee_hit_confirmed',
  COMBAT_HIT_PREDICTED: 'combat:hit_predicted',
  COMBAT_AREA_HIT: 'combat:area_hit',
  // Fired client-side from ProjectileSystem.fireHit on every projectile
  // collision. DamagePresentationService listens to drain the FCT buffer
  // so damage numbers from bot/remote-player projectiles show up when
  // the sprite visually lands (not when the server's npcDamaged packet
  // arrives, which is one round-trip ahead). See
  // docs/architecture/DEVTOOLS_AND_FCT_PIPELINE.md Part 2.
  COMBAT_PROJECTILE_LANDED: 'combat:projectile_landed',
  /** A cast/channel died early (interrupt / movement-cancel / overwrite).
   *  CastTimelineSystem emits; VFXSystem tears down the cast's live channel
   *  visuals (beam-builders spec §6 integration F — before this, an
   *  interrupted 5s drain burned its beam for the full authored duration). */
  COMBAT_CAST_CANCELLED: 'combat:cast_cancelled',
  COMBAT_PROJECTILE_LAUNCHED: 'combat:projectile_launched',
  COMBAT_PROJECTILE_SHOT_LAUNCHED: 'combat:projectile_shot_launched',
  /** Client-only observer muzzle candidate; the release owner emits the visible cue once. */
  VFX_REMOTE_PROJECTILE_RELEASE: 'vfx:remote_projectile_release',

  // Network lifecycle events
  NETWORK_CONNECTION_LOST: 'network:connection_lost',
  NETWORK_RECONNECTING: 'network:reconnecting',
  NETWORK_CONNECTION_RESTORED: 'network:connection_restored',
  NETWORK_CONNECTION_FAILED: 'network:connection_failed',
  /** Always-on FreezeSentinel tripped — payload is a FreezeEventWire. Drives
   *  the in-game corner tag (ConnectionStatusOverlay). */
  SYSTEM_FREEZE: 'system:freeze',

  // ===== T3.3 name-SSOT additions (2026-06-22) — every shared-bus event keyed here =====
  // Tags: "WIP" = scaffolded feature (EVENT_WIP_REGISTER.md); "missing-wire (E3)" = emitted/listened
  // but the other side is unwired (a real fix landing in E3). All are typed regardless.

  // net
  NET_CONNECTED: 'net:connected',
  NET_CONNECT_INTENT: 'net:connectIntent',
  NET_DEBUG_STATE_INTENT: 'net:debugStateIntent',
  NET_DISCONNECTED: 'net:disconnected',
  NET_DISCONNECT_INTENT: 'net:disconnectIntent',
  NET_FORCE_SYNC_STATS_INTENT: 'net:forceSyncStatsIntent',
  NET_GUILD_JOIN: 'net:guildJoin',  // WIP — see EVENT_WIP_REGISTER.md
  NET_GUILD_LEAVE: 'net:guildLeave',  // WIP — see EVENT_WIP_REGISTER.md
  NET_GUILD_UPDATE: 'net:guildUpdate',  // WIP — see EVENT_WIP_REGISTER.md
  NET_INTERACTABLE_JOIN: 'net:interactableJoin',
  NET_INTERACTABLE_LEAVE: 'net:interactableLeave',
  NET_INTERACTABLE_UPDATE: 'net:interactableUpdate',
  /** BIG-EQUIP-VIS-01 S5 — local authoritative equipment projection from the
   *  nested `player.equipment` schema subtree. Emitted by NetworkSyncSystem;
   *  consumed by PlayerManager to set the transient top-level
   *  `player.equipmentVisuals` and request exactly one texture refresh. */
  NET_LOCAL_EQUIPMENT_VISUALS: 'net:localEquipmentVisuals',
  NET_MESSAGE_ABILITY_LEARNED: 'net:message:abilityLearned',  // a grant taught an ability (server grantAbility.ts → banner)
  NET_MESSAGE_ABILITY_TRAINING_AVAILABLE: 'net:message:abilityTrainingAvailable',  // level eligibility (server → trainer notice/FTUE)
  NET_MESSAGE_ABILITY_CASTED: 'net:message:abilityCasted',  // server broadcast re-emitted as combat:abilityCasted for observers
  NET_MESSAGE_ABILITY_CAST_CANCELLED: 'net:message:abilityCastCancelled',
  VFX_STOP_INSTANCE: 'vfx:stop_instance',
  NET_MESSAGE_CHAT_MESSAGE: 'net:message:chatMessage',
  NET_MESSAGE_PLAYER_LEVELED_UP: 'net:message:playerLeveledUp',  // room-wide level-up (server/utils/progression.ts → LevelUpVFXHandler)
  NET_MESSAGE_DEBUG_COMBAT: 'net:message:debugCombat',
  NET_MESSAGE_FORCE_SYNC_STATS: 'net:message:forceSyncStats',
  NET_MESSAGE_GOLD_RECEIVED: 'net:message:goldReceived',
  NET_MESSAGE_HEAVY_FIELDS_SNAPSHOT: 'net:message:heavyFieldsSnapshot',
  NET_MESSAGE_ABILITY_CHARGES_UPDATE: 'net:message:abilityChargesUpdate',
  NET_MESSAGE_CHEST_RESULT: 'net:message:chestResult',
  NET_MESSAGE_HOTBAR_UPDATE: 'net:message:hotbarUpdate',
  NET_MESSAGE_HOTBAR_NOTICE: 'net:message:hotbarNotice',
  NET_MESSAGE_KEY_BINDINGS: 'net:message:keyBindings',
  NET_MESSAGE_LEARNED_ABILITIES_UPDATE: 'net:message:learnedAbilitiesUpdate',
  NET_MESSAGE_LEARN_ABILITY_RESULT: 'net:message:learnAbilityResult',
  NET_MESSAGE_TALENT_STATE_UPDATE: 'net:message:talentStateUpdate',
  NET_MESSAGE_SPEND_TALENT_RESULT: 'net:message:spendTalentResult',
  NET_MESSAGE_PROGRESSION_COST_OFFERS: 'net:message:progressionCostOffers',
  NET_MESSAGE_RESPEC_TALENTS_RESULT: 'net:message:respecTalentsResult',
  NET_MESSAGE_RESPEC_STATS_RESULT: 'net:message:respecStatsResult',
  NET_MESSAGE_LEVEL_UP: 'net:message:levelUp',  // missing-wire (E3)
  NET_MESSAGE_LOOT_RECEIVED: 'net:message:lootReceived',
  NET_MESSAGE_LOOT_SPAWNED: 'net:message:lootSpawned',
  NET_MESSAGE_LOOT_NEARBY_RESULT: 'net:message:lootNearbyResult',
  NET_MESSAGE_LOOT_VISUAL_DEPARTURE: 'net:message:lootVisualDeparture',
  NET_MESSAGE_MAP_CHANGED: 'net:message:mapChanged',
  NET_MESSAGE_MOB_ABILITY_WINDUP: 'net:message:mobAbilityWindup',
  NET_MESSAGE_MOB_ATTACK_DIAGNOSTIC: 'net:message:mobAttackDiagnostic',
  NET_MESSAGE_MOB_BUMP_DIAGNOSTIC: 'net:message:mobBumpDiagnostic',
  NET_MESSAGE_MOB_RESPAWNED: 'net:message:mobRespawned',
  NET_MESSAGE_MOB_RESPAWNING: 'net:message:mobRespawning',
  NET_MESSAGE_NETWORK_DIAGNOSTICS: 'net:message:networkDiagnostics',
  NET_MESSAGE_NPC_DIALOGUE: 'net:message:npcDialogue',
  NET_MESSAGE_GM_EVENT_ANNOUNCEMENT: 'net:message:gmEventAnnouncement',  // GM Race A.6 (countdown audio/VFX source)
  NET_MESSAGE_GM_EVENT_CANCELLED: 'net:message:gmEventCancelled',  // GM Race A.6 (course teardown)
  NET_MESSAGE_GM_EVENT_ENDED: 'net:message:gmEventEnded',  // GM Race A.6 (course teardown)
  NET_MESSAGE_GM_EVENT_PHASE_CHANGED: 'net:message:gmEventPhaseChanged',  // GM Race A.6 (staging→live GO)
  NET_MESSAGE_GM_EVENT_RACE_FINISH: 'net:message:gmEventRaceFinish',  // GM Race A.3
  NET_MESSAGE_GM_EVENT_RACE_PLAYER_STATE: 'net:message:gmEventRacePlayerState',  // GM Race A.3
  NET_MESSAGE_PLAYER_KNOCKBACK: 'net:message:playerKnockback',
  NET_MESSAGE_PLAYER_TELEPORTED: 'net:message:playerTeleported',
  NET_MESSAGE_PROJECTILE_LAUNCHED: 'net:message:projectileLaunched',
  NET_MESSAGE_PROJECTILE_REJECTED: 'net:message:projectileRejected',
  NET_MESSAGE_PROJECTILE_SHOT_LAUNCHED: 'net:message:projectileShotLaunched',
  NET_MESSAGE_PROJECTILE_SHOT_CONTACT: 'net:message:projectileShotContact',
  NET_MESSAGE_POSITION_CORRECTION: 'net:message:positionCorrection',  // missing-wire (E3)
  NET_MESSAGE_QUEST_ACCEPTED: 'net:message:questAccepted',
  NET_MESSAGE_QUEST_COMPLETED: 'net:message:questCompleted',
  NET_MESSAGE_TIMED_RUN_STATE: 'net:message:timedRunState',
  // ── GM Race broadcast relays (A.2/A.3 — ECS_EVENTBUS §1.2). Deliberately
  //    absent: net:message:race:hazardHits (batch) — FUTURE post-MVP (G-D2). ──
  NET_MESSAGE_RACE_ABILITY_GATE: 'net:message:race:abilityGate',
  NET_MESSAGE_RACE_ABILITY_GATE_CLEAR: 'net:message:race:abilityGateClear',
  NET_MESSAGE_RACE_DNF: 'net:message:race:dnf',
  NET_MESSAGE_RACE_GATE_OPEN: 'net:message:race:gateOpen',  // start gate opens at GO (staging→live)
  NET_MESSAGE_RACE_HAZARD_HIT: 'net:message:race:hazardHit',  // per-hit VFX observer broadcast (individual — G-D2)
  NET_MESSAGE_RACE_HAZARD_IMPULSE: 'net:message:race:hazardImpulse',  // GM Race A.2 (relay of `race:hazardImpulse`)
  NET_MESSAGE_RACE_HAZARDS_LIST: 'net:message:race:hazardsList',
  NET_MESSAGE_RACE_POWERUP_EFFECT_END: 'net:message:race:powerupEffectEnd',
  NET_MESSAGE_RACE_POWERUP_EFFECT_START: 'net:message:race:powerupEffectStart',
  NET_MESSAGE_RACE_POWERUP_PICKED: 'net:message:race:powerupPicked',
  NET_MESSAGE_RACE_POWERUP_RESPAWN: 'net:message:race:powerupRespawn',
  NET_MESSAGE_RACE_POWERUPS_LIST: 'net:message:race:powerupsList',
  NET_MESSAGE_RACE_SLOW_TRAP_EXPIRED: 'net:message:race:slowTrapExpired',
  NET_MESSAGE_RACE_SLOW_TRAP_PLACED: 'net:message:race:slowTrapPlaced',
  NET_MESSAGE_RACE_SLOW_TRAP_TRIGGERED: 'net:message:race:slowTrapTriggered',
  NET_MESSAGE_RACE_STAT_OVERRIDE: 'net:message:race:statOverride',
  NET_MESSAGE_RACE_STAT_RESTORE: 'net:message:race:statRestore',
  NET_MESSAGE_SIM_LIVE: 'net:message:simLive',
  NET_MOUNT_JOIN: 'net:mountJoin',  // WIP — see EVENT_WIP_REGISTER.md
  NET_MOUNT_LEAVE: 'net:mountLeave',  // WIP — see EVENT_WIP_REGISTER.md
  NET_MOUNT_UPDATE: 'net:mountUpdate',  // WIP — see EVENT_WIP_REGISTER.md
  NET_NPC_JOIN: 'net:npcJoin',
  NET_NPC_LEAVE: 'net:npcLeave',
  NET_NPC_UPDATE: 'net:npcUpdate',
  NET_PERIODIC_SYNC: 'net:periodicSync',
  NET_PET_JOIN: 'net:petJoin',
  NET_PET_LEAVE: 'net:petLeave',
  NET_PET_UPDATE: 'net:petUpdate',
  NET_REMOTE_PLAYER_JOIN: 'net:remotePlayerJoin',
  NET_REMOTE_PLAYER_LEAVE: 'net:remotePlayerLeave',
  NET_REMOTE_PLAYER_UPDATE: 'net:remotePlayerUpdate',
  NET_ROOM_META: 'net:roomMeta',

  // GM race — client-internal gameplay events (NOT broadcast relays; emitted
  // by client systems so UI/physics/VFX/audio can react without coupling).
  // ECS_EVENTBUS §1.2. Consumers land in Phase 1-3 (A.4-A.6).
  RACE_CHECKPOINT_REACHED: 'race:checkpointReached',
  RACE_COUNTDOWN_GO: 'race:countdownGo',
  RACE_COUNTDOWN_TICK: 'race:countdownTick',
  RACE_DNF: 'race:dnf',
  RACE_FINISHED: 'race:finished',
  RACE_HAZARD_HIT: 'race:hazardHit',
  RACE_HAZARD_IMPULSE_APPLIED: 'race:hazardImpulseApplied',
  RACE_LAP_COMPLETED: 'race:lapCompleted',
  RACE_POWERUP_EFFECT_END: 'race:powerupEffectEnd',
  RACE_POWERUP_EFFECT_START: 'race:powerupEffectStart',
  RACE_POWERUP_PICKED_UP: 'race:powerupPickedUp',
  RACE_SPECTATOR_CYCLE: 'race:spectatorCycle',

  // vfx
  VFX_ARCANE_BARRAGE_FIRE: 'vfx:arcane_barrage_fire',
  VFX_ARCANE_BARRAGE_ORBIT: 'vfx:arcane_barrage_orbit',
  VFX_BLINK: 'vfx:blink',
  VFX_DAMAGE_NUMBER: 'vfx:damage_number',  // wired: DamagePresentationService.onClientEffectNumber (lifesteal/bonusDamage/healSelf flourishes)
  VFX_DASH_GHOST: 'vfx:dash_ghost',  // travel-dash afterimage tick (DashTravelState → VFXSystem → TrailSystem)
  VFX_DASH_GHOST_LINE: 'vfx:dash_ghost_line',  // instant-blink afterimage line (DashBehavior → VFXSystem → TrailSystem)
  VFX_HITSCAN_BEAM: 'vfx:hitscan_beam',
  VFX_MOB_PROJECTILE: 'vfx:mob_projectile',
  VFX_LUCKY_SEVEN_FIRE: 'vfx:lucky_seven_fire',
  VFX_LUCKY_SEVEN_ORBIT: 'vfx:lucky_seven_orbit',
  VFX_MELEE_SWING: 'vfx:melee_swing',
  /** Plan 29: a cone ability's real hit wedge (apex = body centre). */
  VFX_AREA_CONE: 'vfx:area_cone',
  VFX_PACT_DOOR: 'vfx:pact_door',  // Pact Door's door frames (PactDoorAbility → PactDoorVFXHandler)
  VFX_RELOAD_STARS: 'vfx:reload_stars',
  VFX_SCREEN_FLASH: 'vfx:screen_flash',
  VFX_SCREEN_SHAKE: 'vfx:screen_shake',
  VFX_SPAWN: 'vfx:spawn',
  VFX_SPAWN_PARTICLES: 'vfx:spawn_particles',  // missing-wire (E3)
  VFX_STATUS_EFFECT: 'vfx:status_effect',
  VFX_STATUS_STACK_UPDATE: 'vfx:status_stack_update',  // missing-wire (E3)
  VFX_SUMMON_EXPIRED: 'vfx:summon_expired',
  VFX_SUMMON_PLACED: 'vfx:summon_placed',
  VFX_SUMMON_TRIGGERED: 'vfx:summon_triggered',
  UI_CONTEXTUAL_INTERACTION: 'ui:contextualInteraction',
  UI_CONTEXTUAL_INTERACTION_ACTIVATE: 'ui:contextualInteraction:activate',
  HUSH_ENTRANCE_WINK: 'hush:entranceWink',

  // combat
  COMBAT_APPLY_EFFECT: 'combat:apply_effect',
  COMBAT_AURA_HEAL: 'combat:aura_heal',
  COMBAT_BONUS_DAMAGE: 'combat:bonus_damage',
  COMBAT_DAMAGE: 'combat:damage',
  COMBAT_KNOCKBACK: 'combat:knockback',
  COMBAT_LIFESTEAL: 'combat:lifesteal',
  COMBAT_MANA_RESTORE: 'combat:mana_restore',
  COMBAT_RESOURCE_EXCHANGE: 'combat:resource_exchange',
  COMBAT_SUMMON_ARMED: 'combat:summon_armed',

  // editor
  EDITOR_ENTITY_SELECTED: 'editor:entitySelected',
  EDITOR_HISTORY_CHANGED: 'editor:historyChanged',
  EDITOR_INVALID_PLACEMENT: 'editor:invalidPlacement',  // missing-wire (E3)
  EDITOR_LEVEL_EXPORTED: 'editor:levelExported',
  EDITOR_LEVEL_LOAD_FAILED: 'editor:levelLoadFailed',  // missing-wire (E3)
  EDITOR_LEVEL_SAVED_AS_NEW: 'editor:levelSavedAsNew',  // missing-wire (E3)
  EDITOR_LEVEL_SAVED_LOCALLY: 'editor:levelSavedLocally',
  EDITOR_LEVEL_SAVE_FAILED: 'editor:levelSaveFailed',
  EDITOR_LEVEL_SYNCED: 'editor:levelSynced',
  EDITOR_PAINT_END: 'editor:paintEnd',
  EDITOR_PREVIEW_ENTITY_MOVE: 'editor:previewEntityMove',
  EDITOR_PREVIEW_SHAPE: 'editor:previewShape',
  EDITOR_SELECTION_CHANGED: 'editor:selectionChanged',
  EDITOR_SELECTION_CLEARED: 'editor:selectionCleared',
  EDITOR_TILE_SELECTED: 'editor:tileSelected',
  EDITOR_ZOOM_CHANGED: 'editor:zoomChanged',
  EDITOR_CONTEXT_MENU: 'editor:contextMenu',
  EDITOR_TEST_PLAY_ENTERED: 'editor:testPlayEntered',
  EDITOR_TEST_PLAY_EXITED: 'editor:testPlayExited',

  // layer
  LAYER_ACTIVE_LAYER_CHANGED: 'layer:activeLayerChanged',
  LAYER_LOCK_CHANGED: 'layer:lockChanged',  // missing-wire (E3)
  LAYER_OPACITY_CHANGED: 'layer:opacityChanged',
  LAYER_VISIBILITY_CHANGED: 'layer:visibilityChanged',

  // player
  PLAYER_ADD_XP_INTENT: 'player:addXPIntent',
  PLAYER_SAVE_INTENT: 'player:saveIntent',  // missing-wire (E3) — consumers wired (save), manual-save UI trigger unbuilt
  PLAYER_DEATH: 'player:death',  // missing-wire (E3)
  PLAYER_DISMISS_PET_INTENT: 'player:dismissPetIntent',  // WIP — see EVENT_WIP_REGISTER.md
  PLAYER_HEAL_INTENT: 'player:healIntent',
  PLAYER_LEVEL_UP_INTENT: 'player:levelUpIntent',
  PLAYER_LOOT_NEARBY_INTENT: 'player:lootNearbyIntent',
  PLAYER_MANA_INTENT: 'player:manaIntent',
  PLAYER_OPEN_CHEST_INTENT: 'player:openChestIntent',
  PLAYER_TAKE_CHEST_INTENT: 'player:takeChestIntent',
  PLAYER_SUMMON_PET_INTENT: 'player:summonPetIntent',  // WIP — see EVENT_WIP_REGISTER.md

  // mob
  MOB_RESPAWN_TIMER_ENDED: 'mob:respawnTimerEnded',  // missing-wire (E3)
  MOB_RESPAWN_TIMER_STARTED: 'mob:respawnTimerStarted',  // missing-wire (E3)
  MOB_RESPAWN_TIMER_UPDATED: 'mob:respawnTimerUpdated',  // missing-wire (E3)

  // ecs
  ECS_ENTITY_ADDED: 'ecs:entityAdded',  // missing-wire (E3)
  ECS_ENTITY_REMOVED: 'ecs:entityRemoved',  // WIP — see EVENT_WIP_REGISTER.md

  // npc
  NPC_DIALOGUE_LEFT_RANGE: 'npc:dialogueLeftRange',
  NPC_INTERACT: 'npc:interact',
  QUEST_CONTEXTUAL_ACTION: 'quest:contextualAction',

  // tutorial
  TUTORIAL_COMPLETE: 'tutorial:complete',
  TUTORIAL_OBJECTIVE_COMPLETED: 'tutorial:objective_completed',
  TUTORIAL_STEP_CHANGED: 'tutorial:step_changed',

  // inventory
  INVENTORY_ITEM_ADDED: 'inventory:itemAdded',  // missing-wire (E3)

  // chat
  CHAT_COMMAND: 'chat:command',

  // dev
  DEV_ACTION: 'dev:action',

  // debug
  DEBUG_FORCE_SNAP_TO_SCHEMA: 'debug:forceSnapToSchema',

  // misc
  REQUEST_MAP_SWITCH: 'requestMapSwitch',
  REQUEST_AUTHORIZED_WORLD_TRANSFER: 'requestAuthorizedWorldTransfer',
  MAP_SERVER_SWITCH_START: 'map:serverSwitchStart',
  MAP_SERVER_SWITCH_COMPLETE: 'map:serverSwitchComplete',
  /** In-place portal trip (plan 25, ?seamlessPortals=1): the page stays, the
   *  scene and room are swapped. Start fires before teardown; complete after the
   *  destination GameScene is ready (ok=false → falling back to a reload). */
  MAP_TRAVEL_START: 'map:travelStart',
  MAP_TRAVEL_COMPLETE: 'map:travelComplete',

  // tested contracts kept as missing-wire (emit is tested + intentional; consumer unbuilt — E3)
  QUEST_ACCEPTED: 'quest:accepted',  // missing-wire (E3) — quest-log/UI consumer
  QUEST_PROGRESS: 'quest:progress',  // missing-wire (E3) — quest progress bar consumer
  PORTAL_BLOCKED: 'map:portalBlocked',  // missing-wire (E3) — blocked-portal overlay (toast already direct)

  // unprefixed live events (legacy names; emitter + listener already agree — keyed by T3.3 E2-B)
  SHOW_XP_GAIN: 'showXPGain',          // useXPFeedback → SceneEventManager XP float
  GAIN_XP: 'gainXP',                   // NPCManager/QuestSystem → ProgressionSystem XP grant
  PLAYER_TELEPORT: 'playerTeleport',   // MapSystem teleporter → EntityEventWiring (distinct from past-tense player:teleported)
} as const;
